import { useState, useEffect, useCallback } from "react";
import {
  RefreshCw, ExternalLink, Eye, EyeOff, CheckCircle, AlertTriangle,
  Plus, Phone, MessageSquare, MessageCircle, Smartphone, Table2 as TableIcon,
  HardDrive, TrendingUp, Mail, Database, Brain, Video, Mic, Cloud,
  Receipt, CreditCard, Hash, Megaphone, Send, Headphones, Music,
  Play, AtSign, Wallet, Image, Flame, Calendar, Landmark, AudioLines,
  Palette, Search, ChevronDown, ChevronUp, Bitcoin, QrCode, X, Wifi, WifiOff,
  Link as LinkIcon, Pencil, Globe, Trash2, Copy
} from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl() + "/api";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const ICON_MAP = {
  brain: Brain, phone: Phone, "message-square": MessageSquare,
  "message-circle": MessageCircle, smartphone: Smartphone, table: TableIcon,
  "hard-drive": HardDrive, "trending-up": TrendingUp, mail: Mail,
  database: Database, video: Video, mic: Mic, "cloud-sun": Cloud,
  receipt: Receipt, "credit-card": CreditCard, palette: Palette,
  landmark: Landmark, "audio-lines": AudioLines, send: Send,
  calendar: Calendar, hash: Hash, megaphone: Megaphone,
  headphones: Headphones, music: Music, play: Play,
  "at-sign": AtSign, bitcoin: Bitcoin, wallet: Wallet,
  "mail-plus": Mail, image: Image, flame: Flame, globe: Globe,
};

const STATUS_CONFIG = {
  connecte: { label: "Connecte", dotClass: "bg-emerald-400", ringClass: "ring-emerald-500/20", textClass: "text-emerald-400" },
  connected: { label: "Connecte", dotClass: "bg-emerald-400", ringClass: "ring-emerald-500/20", textClass: "text-emerald-400" },
  en_test: { label: "En test", dotClass: "bg-amber-400", ringClass: "ring-amber-500/20", textClass: "text-amber-400" },
  a_connecter: { label: "A connecter", dotClass: "bg-slate-500", ringClass: "ring-slate-500/20", textClass: "text-slate-400" },
};

function WhatsAppQRModal({ open, onClose }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("qr"); // "qr" or "pairing"
  const [phoneInput, setPhoneInput] = useState("");
  const [pairingCode, setPairingCode] = useState(null);
  const [pairingLoading, setPairingLoading] = useState(false);
  const [pairingError, setPairingError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/whatsapp/qr`);
      if (res.ok) setStatus(await res.json());
    } catch (e) {
      console.error("Erreur QR:", e);
    }
    setLoading(false);
  }, []);

  const requestPairingCode = async () => {
    if (!phoneInput.trim()) return;
    setPairingLoading(true);
    setPairingError(null);
    setPairingCode(null);
    try {
      const clean = phoneInput.replace(/[^0-9]/g, '');
      const res = await authFetch(`${API}/whatsapp/pairing-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: clean }),
      });
      const data = await res.json();
      if (data.success && data.pairingCode) {
        setPairingCode(data.pairingCode);
      } else {
        setPairingError(data.error || data.detail || "Erreur inconnue");
      }
    } catch (e) {
      setPairingError(e.message);
    }
    setPairingLoading(false);
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setMode("qr");
    setPairingCode(null);
    setPairingError(null);
    fetchStatus();
    const interval = setInterval(fetchStatus, 4000);
    return () => clearInterval(interval);
  }, [open, fetchStatus]);

  if (!open) return null;

  const isReady = status?.connectedAs?.number;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="whatsapp-qr-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-base">WhatsApp Baileys</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors" data-testid="close-qr-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center py-8">
            <RefreshCw className="w-8 h-8 text-slate-500 animate-spin" />
            <p className="text-sm text-slate-400 mt-3">Chargement...</p>
          </div>
        ) : isReady ? (
          <div className="flex flex-col items-center py-6">
            <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mb-3">
              <Wifi className="w-7 h-7 text-emerald-400" />
            </div>
            <p className="text-emerald-400 font-semibold text-sm">Connecte</p>
            <p className="text-slate-400 text-xs mt-1">{status.connectedAs.name} ({status.connectedAs.number})</p>
          </div>
        ) : mode === "qr" ? (
          <div className="flex flex-col items-center">
            {status?.qrCodeDataUrl ? (
              <>
                <p className="text-sm text-slate-300 mb-4 text-center">Scannez ce QR code avec WhatsApp sur votre telephone</p>
                <div className="bg-white rounded-xl p-3 mb-4">
                  <img src={status.qrCodeDataUrl} alt="QR Code WhatsApp" className="w-56 h-56" data-testid="whatsapp-qr-image" />
                </div>
                <p className="text-xs text-slate-500 text-center">Le QR code se rafraichit automatiquement</p>
              </>
            ) : (
              <div className="flex flex-col items-center py-6">
                <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center mb-3">
                  <WifiOff className="w-7 h-7 text-amber-400" />
                </div>
                <p className="text-amber-400 font-semibold text-sm">En attente</p>
                <p className="text-slate-400 text-xs mt-2 text-center">QR code en cours de generation...</p>
                <button onClick={fetchStatus} className="mt-3 px-4 py-1.5 bg-slate-700 rounded-lg text-xs text-white hover:bg-slate-600 transition-colors" data-testid="refresh-qr-btn">
                  Rafraichir
                </button>
              </div>
            )}
            <button
              onClick={() => setMode("pairing")}
              className="mt-4 px-4 py-2 text-xs text-emerald-400 hover:text-emerald-300 underline transition-colors"
              data-testid="switch-pairing-mode"
            >
              QR bloque ? Connecter avec un numero de telephone
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <p className="text-sm text-slate-300 mb-4 text-center">
              Entrez votre numero WhatsApp pour recevoir un code de liaison
            </p>
            <div className="w-full mb-3">
              <input
                type="text"
                placeholder="33755545969 (sans +)"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-800 border border-white/10 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-500/50"
                data-testid="pairing-phone-input"
              />
            </div>
            <button
              onClick={requestPairingCode}
              disabled={pairingLoading || !phoneInput.trim()}
              className="w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded-xl text-white text-sm font-medium transition-colors"
              data-testid="request-pairing-btn"
            >
              {pairingLoading ? "Generation..." : "Obtenir le code"}
            </button>
            {pairingCode && (
              <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-center w-full" data-testid="pairing-code-display">
                <p className="text-xs text-slate-400 mb-1">Votre code de liaison :</p>
                <p className="text-3xl font-mono font-bold text-emerald-400 tracking-widest">{pairingCode}</p>
                <p className="text-xs text-slate-500 mt-2">
                  Allez dans WhatsApp &gt; Appareils lies &gt; Lier un appareil &gt; Connecter avec un numero de telephone
                </p>
              </div>
            )}
            {pairingError && (
              <p className="mt-3 text-xs text-red-400 text-center" data-testid="pairing-error">{pairingError}</p>
            )}
            <button
              onClick={() => { setMode("qr"); setPairingCode(null); setPairingError(null); }}
              className="mt-4 px-4 py-2 text-xs text-slate-400 hover:text-white underline transition-colors"
              data-testid="switch-qr-mode"
            >
              Revenir au QR code
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function IntegrationCard({ item, onUrlUpdate, onRefresh }) {
  const [showKey, setShowKey] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [oauthConnecting, setOauthConnecting] = useState(false);
  const [extraUrls, setExtraUrls] = useState(item.extra_urls || []);
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyDesc, setNewKeyDesc] = useState("");
  const [createdKey, setCreatedKey] = useState(null);
  const [keysLoading, setKeysLoading] = useState(false);
  const Icon = ICON_MAP[item.icon] || Database;
  const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.a_connecter;
  const isBaileys = item.id === "whatsapp_baileys";
  const hasOAuth = item.oauth_support;
  const isLanding = item.id === "landing_page";
  const hasManageKeys = item.manage_keys;

  const loadApiKeys = async () => {
    setKeysLoading(true);
    try {
      const res = await authFetch(`${API}/public/admin/keys`);
      if (res.ok) { const d = await res.json(); setApiKeys(d.keys || []); }
    } catch (e) { console.error(e); }
    setKeysLoading(false);
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) return;
    try {
      const res = await authFetch(`${API}/public/admin/keys`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newKeyName.trim(), description: newKeyDesc.trim() }),
      });
      if (res.ok) {
        const d = await res.json();
        setCreatedKey(d.key);
        setNewKeyName("");
        setNewKeyDesc("");
        loadApiKeys();
        if (onRefresh) setTimeout(onRefresh, 500);
      }
    } catch (e) { console.error(e); }
  };

  const toggleApiKey = async (name) => {
    await authFetch(`${API}/public/admin/keys/${encodeURIComponent(name)}/toggle`, { method: "PUT" });
    loadApiKeys();
    if (onRefresh) setTimeout(onRefresh, 500);
  };

  const deleteApiKey = async (name) => {
    if (!window.confirm(`Desactiver la cle "${name}" ?`)) return;
    await authFetch(`${API}/public/admin/keys/${encodeURIComponent(name)}`, { method: "DELETE" });
    loadApiKeys();
    if (onRefresh) setTimeout(onRefresh, 500);
  };

  const handleSaveUrl = async () => {
    const trimmed = urlValue.trim();
    if (!trimmed) { setEditingUrl(false); return; }
    // For landing page: don't save if URL already exists
    if (isLanding && extraUrls.includes(trimmed)) { setEditingUrl(false); setUrlValue(""); return; }
    setSaving(true);
    try {
      const res = await authFetch(`${API}/integrations/${item.id}/update-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: trimmed }),
      });
      if (res.ok) {
        if (isLanding) {
          const data = await res.json();
          setExtraUrls(data.all_urls || [...extraUrls, trimmed]);
        }
        if (onUrlUpdate) onUrlUpdate(trimmed);
        setEditingUrl(false);
        setUrlValue("");
        if (onRefresh) setTimeout(onRefresh, 300);
      }
    } catch {}
    setSaving(false);
  };

  const handleDeleteUrl = async (url) => {
    try {
      const res = await authFetch(`${API}/integrations/landing_page/urls`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      if (res.ok) {
        const data = await res.json();
        setExtraUrls(data.remaining_urls || extraUrls.filter(u => u !== url));
        if (onRefresh) setTimeout(onRefresh, 300);
      }
    } catch {}
  };

  const handleOAuthConnect = async () => {
    setOauthConnecting(true);
    try {
      const res = await authFetch(`${API}/google-ads/auth/url`);
      const data = await res.json();
      if (data.auth_url) {
        const popup = window.open(data.auth_url, 'google-ads-auth', 'width=600,height=700');
        if (!popup) { setOauthConnecting(false); return; }
        const handleMsg = (e) => {
          if (e.data?.type === 'google-ads-auth') {
            setOauthConnecting(false);
            window.removeEventListener('message', handleMsg);
            if (onRefresh) setTimeout(onRefresh, 500);
          }
        };
        window.addEventListener('message', handleMsg);
        const chk = setInterval(() => {
          if (popup?.closed) {
            clearInterval(chk);
            setOauthConnecting(false);
            if (onRefresh) setTimeout(onRefresh, 1000);
          }
        }, 1000);
      }
    } catch { setOauthConnecting(false); }
  };

  const handleOAuthDisconnect = async () => {
    if (!window.confirm("Deconnecter Google Ads OAuth ?")) return;
    await authFetch(`${API}/google-ads/auth/disconnect`, { method: "POST" });
    if (onRefresh) setTimeout(onRefresh, 500);
  };

  return (
    <>
      <div
        data-testid={`integration-card-${item.id}`}
        className={`group relative bg-slate-800/40 backdrop-blur-sm border border-white/5 rounded-xl p-4 transition-all duration-300 hover:border-white/10 hover:bg-slate-800/60 ${isBaileys ? "cursor-pointer" : ""}`}
        onClick={isBaileys ? () => setShowQR(true) : undefined}
      >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
          style={{ backgroundColor: `${item.color}18` }}
        >
          <Icon className="w-5 h-5" style={{ color: item.color }} />
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-white text-sm truncate">{item.name}</h4>
            {/* Status dot */}
            <span className={`w-2 h-2 rounded-full shrink-0 ${statusCfg.dotClass}`} />
          </div>
          <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">{item.description}</p>

          {/* Key preview */}
          {item.key_preview && (
            <div className="mt-2 flex items-center gap-1.5">
              <button
                onClick={(e) => { e.stopPropagation(); setShowKey(!showKey); }}
                className="text-slate-500 hover:text-slate-300 transition-colors"
                data-testid={`toggle-key-${item.id}`}
              >
                {showKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
              <span className="font-mono text-[11px] text-slate-500 truncate">
                {showKey ? item.key_preview : "**********"}
              </span>
            </div>
          )}

          {/* Editable URL field */}
          {item.editable_url && (
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              {/* Multi-URL list for landing page */}
              {isLanding && extraUrls.length > 0 && (
                <div className="space-y-1 mb-1.5">
                  {extraUrls.map((u, i) => (
                    <div key={i} className="flex items-center gap-1.5 group/url">
                      <LinkIcon className="w-3 h-3 text-emerald-400/60 shrink-0" />
                      <span className="font-mono text-[10px] text-slate-400 truncate flex-1" title={u}>{u}</span>
                      <button
                        onClick={() => handleDeleteUrl(u)}
                        className="opacity-0 group-hover/url:opacity-100 text-red-400/60 hover:text-red-400 transition-all"
                        data-testid={`delete-url-${i}`}
                        title="Supprimer"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {editingUrl ? (
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={urlValue}
                    onChange={(e) => setUrlValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleSaveUrl(); if (e.key === "Escape") { setEditingUrl(false); setUrlValue(""); } }}
                    className="flex-1 bg-slate-900/80 border border-emerald-500/30 rounded-md px-2 py-1 text-[11px] text-white font-mono outline-none focus:border-emerald-500/60"
                    placeholder={isLanding ? "https://votre-crm.com/api/webhook/landing-lead" : "URL"}
                    data-testid={`url-input-${item.id}`}
                    autoFocus
                  />
                  <button onClick={handleSaveUrl} disabled={saving} className="px-2 py-1 bg-emerald-600/30 border border-emerald-500/30 rounded-md text-[10px] text-emerald-300 hover:bg-emerald-600/50 transition-colors" data-testid={`url-save-${item.id}`}>
                    {saving ? "..." : isLanding ? "Ajouter" : "OK"}
                  </button>
                  <button onClick={() => { setEditingUrl(false); setUrlValue(""); }} className="px-1.5 py-1 text-slate-500 hover:text-slate-300 text-[10px]">
                    Annuler
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditingUrl(true)}
                  className="flex items-center gap-1.5 text-[11px] font-mono text-slate-500 hover:text-emerald-300 transition-colors truncate max-w-full"
                  data-testid={`url-edit-${item.id}`}
                  title={isLanding ? "Ajouter une URL webhook" : "Modifier l'URL"}
                >
                  {isLanding ? (
                    <>
                      <Plus className="w-3 h-3 shrink-0" />
                      <span>Ajouter une URL webhook</span>
                    </>
                  ) : (
                    <>
                      <LinkIcon className="w-3 h-3 shrink-0" />
                      <span className="truncate">{item.config_url || "Non configuree"}</span>
                      <Pencil className="w-2.5 h-2.5 shrink-0 opacity-0 group-hover:opacity-100" />
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Google Ads OAuth connect/disconnect */}
          {hasOAuth && (
            <div className="mt-2.5 space-y-1.5" onClick={(e) => e.stopPropagation()}>
              {item.access_level && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-medium">{item.access_level}</span>
                  {item.manager_id && <span className="text-[10px] text-slate-500">MCC: {item.manager_id}</span>}
                </div>
              )}
              {item.oauth_email && (
                <p className="text-[11px] text-slate-400 font-mono">{item.oauth_email}</p>
              )}
              <div className="flex items-center gap-2 pt-0.5">
                {item.oauth_status === "connected" ? (
                  <button
                    onClick={handleOAuthDisconnect}
                    className="px-2.5 py-1 bg-red-500/15 border border-red-500/25 rounded-md text-[11px] text-red-400 hover:bg-red-500/25 transition-colors"
                    data-testid="google-ads-disconnect-btn"
                  >
                    Deconnecter OAuth
                  </button>
                ) : (
                  <button
                    onClick={handleOAuthConnect}
                    disabled={oauthConnecting}
                    className="px-2.5 py-1 bg-yellow-500/15 border border-yellow-500/30 rounded-md text-[11px] text-yellow-300 hover:bg-yellow-500/25 disabled:opacity-50 transition-colors font-medium"
                    data-testid="google-ads-connect-btn"
                  >
                    {oauthConnecting ? "Connexion..." : item.oauth_status === "expired" ? "Reconnecter OAuth" : "Connecter OAuth"}
                  </button>
                )}
                {item.oauth_status === "expired" && (
                  <span className="text-[10px] text-amber-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Token expire
                  </span>
                )}
                {item.oauth_status === "connected" && (
                  <span className="text-[10px] text-emerald-400 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> OAuth actif
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Baileys connect hint */}
          {isBaileys && (
            <div className="mt-2 flex items-center gap-1.5 text-teal-400">
              <QrCode className="w-3.5 h-3.5" />
              <span className="text-[11px] font-medium">Cliquer pour connecter / voir QR</span>
            </div>
          )}

          {/* API Keys management button */}
          {hasManageKeys && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowApiKeys(!showApiKeys); if (!showApiKeys) loadApiKeys(); }}
              className="mt-2 flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 transition-colors"
              data-testid="manage-api-keys-btn"
            >
              <LinkIcon className="w-3.5 h-3.5" />
              <span className="text-[11px] font-medium">{showApiKeys ? "Fermer" : "Gerer les cles API"}</span>
            </button>
          )}
        </div>

        {/* External link */}
        {item.config_url && (
          <a
            href={item.config_url}
            target="_blank"
            rel="noopener noreferrer"
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-white/5 text-slate-500 hover:text-white"
            title="Ouvrir la console"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>

      {/* API Keys Panel */}
      {hasManageKeys && showApiKeys && (
        <div className="mt-3 pt-3 border-t border-white/5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="api-keys-panel">
          {keysLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-xs"><RefreshCw className="w-3 h-3 animate-spin" /> Chargement...</div>
          ) : (
            <>
              {/* Existing keys */}
              {apiKeys.length > 0 && (
                <div className="space-y-2">
                  {apiKeys.map((k, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-900/60 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-white">{k.name}</span>
                          <span className={`w-1.5 h-1.5 rounded-full ${k.active ? "bg-emerald-400" : "bg-red-400"}`} />
                        </div>
                        <div className="text-[10px] text-slate-500 font-mono">{k.key_preview}</div>
                        {k.last_used && <div className="text-[10px] text-slate-600">Dernier appel: {new Date(k.last_used).toLocaleString("fr-FR")}</div>}
                        {k.usage_count > 0 && <div className="text-[10px] text-slate-600">{k.usage_count} appels</div>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => toggleApiKey(k.name)} className={`p-1.5 rounded-lg transition-colors ${k.active ? "hover:bg-amber-500/20 text-amber-400" : "hover:bg-emerald-500/20 text-emerald-400"}`} title={k.active ? "Desactiver" : "Activer"}>
                          {k.active ? <WifiOff className="w-3.5 h-3.5" /> : <Wifi className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => deleteApiKey(k.name)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors" title="Supprimer">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Created key alert */}
              {createdKey && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3" data-testid="new-key-alert">
                  <p className="text-xs text-cyan-300 font-medium mb-1">Nouvelle cle creee — copiez-la maintenant :</p>
                  <div className="flex items-center gap-2">
                    <code className="text-[11px] text-cyan-200 font-mono bg-slate-900/80 px-2 py-1 rounded flex-1 break-all">{createdKey}</code>
                    <button onClick={() => { navigator.clipboard.writeText(createdKey); }} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-colors shrink-0" title="Copier">
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button onClick={() => setCreatedKey(null)} className="text-[10px] text-slate-500 hover:text-slate-300 mt-2">Fermer</button>
                </div>
              )}

              {/* Create new key */}
              <div className="bg-slate-900/40 rounded-lg p-3 space-y-2">
                <p className="text-xs text-slate-400 font-medium">Nouvelle cle</p>
                <input
                  type="text"
                  placeholder="Nom (ex: prime-adapt-seniors)"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  className="w-full px-3 py-1.5 bg-slate-800/60 border border-white/5 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/30"
                  data-testid="new-key-name"
                />
                <input
                  type="text"
                  placeholder="Description (optionnel)"
                  value={newKeyDesc}
                  onChange={(e) => setNewKeyDesc(e.target.value)}
                  className="w-full px-3 py-1.5 bg-slate-800/60 border border-white/5 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/30"
                  data-testid="new-key-desc"
                />
                <button
                  onClick={createApiKey}
                  disabled={!newKeyName.trim()}
                  className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 rounded-lg text-xs font-medium transition-colors"
                  data-testid="create-key-btn"
                >
                  Creer la cle
                </button>
              </div>

              {/* Endpoint info */}
              <div className="bg-slate-900/40 rounded-lg p-3">
                <p className="text-xs text-slate-400 font-medium mb-1">Endpoint</p>
                <code className="text-[11px] text-cyan-300 font-mono">GET {getApiUrl()}/api/public/calls</code>
                <p className="text-[10px] text-slate-500 mt-1">Header: <code className="text-slate-400">X-API-Key: votre_cle</code></p>
                <p className="text-[10px] text-slate-500">Params: <code className="text-slate-400">?date_from=YYYY-MM-DD&date_to=YYYY-MM-DD&phone=06...&direction=in|out&limit=200</code></p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
    {isBaileys && <WhatsAppQRModal open={showQR} onClose={() => setShowQR(false)} />}
    </>
  );
}

function SectionBlock({ title, subtitle, count, badge, badgeColor, icon: SectionIcon, items, defaultOpen = true, onRefresh }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="mb-6" data-testid={`section-${badge.toLowerCase().replace(/\s/g, "-")}`}>
      {/* Section header */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between group/section mb-3 cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${badgeColor}`}>
            <SectionIcon className="w-4 h-4 text-white" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="text-white font-bold text-sm">{title}</h3>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${badgeColor} text-white`}>
                {count}
              </span>
            </div>
            <p className="text-xs text-slate-500">{subtitle}</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>

      {/* Cards grid */}
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((item) => (
            <IntegrationCard key={item.id} item={item} onUrlUpdate={() => onRefresh?.()} onRefresh={onRefresh} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function IntegrationsClesDashboard({ isPlatformAdmin = true }) {
  if (!isPlatformAdmin) {
    return <TenantIntegrationsView />;
  }

  return <PlatformIntegrationsView />;
}

// ═══════════════════════════════════════════════════
// VUE TENANT — Mode selector par integration
// ═══════════════════════════════════════════════════

function TenantIntegrationCard({ item, onUpdate }) {
  const [mode, setMode] = useState(item.mode || "platform");
  const [ownKey, setOwnKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showWebhooks, setShowWebhooks] = useState(false);
  const [webhookTokens, setWebhookTokens] = useState([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [newTokenName, setNewTokenName] = useState("");
  const [createdToken, setCreatedToken] = useState(null);
  const Icon = ICON_MAP[item.icon] || Database;
  const hasManageWebhooks = item.manage_webhooks;

  const loadWebhookTokens = async () => {
    setWebhooksLoading(true);
    try {
      const res = await authFetch(`${API}/integrations/my-webhook-tokens`);
      if (res.ok) { const d = await res.json(); setWebhookTokens(d.tokens || []); }
    } catch (e) { console.error(e); }
    setWebhooksLoading(false);
  };

  const createWebhookToken = async () => {
    if (!newTokenName.trim()) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API}/integrations/my-webhook-tokens`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newTokenName.trim() }),
      });
      if (res.ok) {
        const d = await res.json();
        setCreatedToken(d);
        setNewTokenName("");
        loadWebhookTokens();
        if (onUpdate) setTimeout(onUpdate, 500);
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const toggleWebhookToken = async (name) => {
    await authFetch(`${API}/integrations/my-webhook-tokens/${encodeURIComponent(name)}/toggle`, { method: "PUT" });
    loadWebhookTokens();
  };

  const deleteWebhookToken = async (name) => {
    if (!window.confirm(`Supprimer le token "${name}" ?`)) return;
    await authFetch(`${API}/integrations/my-webhook-tokens/${encodeURIComponent(name)}`, { method: "DELETE" });
    loadWebhookTokens();
    if (onUpdate) setTimeout(onUpdate, 500);
  };

  const modeLabels = {
    platform: { label: "Cle plateforme", color: "text-emerald-400", bg: "bg-emerald-500/15 border-emerald-500/25", dot: "bg-emerald-400" },
    own_key: { label: "Propre cle", color: "text-blue-400", bg: "bg-blue-500/15 border-blue-500/25", dot: "bg-blue-400" },
    disabled: { label: "Desactive", color: "text-slate-500", bg: "bg-slate-500/15 border-slate-500/25", dot: "bg-slate-500" },
  };
  const currentMode = modeLabels[mode] || modeLabels.platform;

  const handleModeChange = async (newMode) => {
    if (newMode === mode) return;
    if (newMode === "own_key" && !item.has_own_key) {
      setMode(newMode);
      setEditing(true);
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch(`${API}/integrations/tenant-config/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: newMode }),
      });
      if (res.ok) {
        setMode(newMode);
        setEditing(false);
        if (onUpdate) onUpdate();
      }
    } catch {}
    setSaving(false);
  };

  const handleSaveOwnKey = async () => {
    if (!ownKey.trim()) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API}/integrations/tenant-config/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "own_key", own_key: ownKey.trim() }),
      });
      if (res.ok) {
        setMode("own_key");
        setEditing(false);
        setOwnKey("");
        if (onUpdate) onUpdate();
      }
    } catch {}
    setSaving(false);
  };

  const isDisabled = mode === "disabled";

  return (
    <div
      data-testid={`tenant-integration-${item.id}`}
      className={`relative bg-slate-800/40 backdrop-blur-sm border border-white/5 rounded-xl p-4 transition-all duration-300 ${isDisabled ? "opacity-50" : "hover:border-white/10 hover:bg-slate-800/60"}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${item.color}18` }}
        >
          <Icon className="w-5 h-5" style={{ color: isDisabled ? "#64748b" : item.color }} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h4 className={`font-semibold text-sm truncate ${isDisabled ? "text-slate-500" : "text-white"}`}>{item.name}</h4>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed line-clamp-2 mb-3">{item.description}</p>

          {/* Mode selector */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {["platform", "own_key", "disabled"].map((m) => (
              <button
                key={m}
                onClick={() => handleModeChange(m)}
                disabled={saving || item.platform_only}
                data-testid={`mode-${m}-${item.id}`}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-all ${
                  mode === m
                    ? modeLabels[m].bg + " " + modeLabels[m].color
                    : "border-transparent text-slate-500 hover:text-slate-300 hover:bg-slate-700/50"
                } ${saving ? "opacity-50" : ""}`}
              >
                {modeLabels[m].label}
              </button>
            ))}
          </div>

          {/* Platform mode indicator */}
          {mode === "platform" && (
            <div className="mt-2 flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-[11px] text-emerald-400/80">Utilise la cle partagee de la plateforme</span>
            </div>
          )}

          {/* Own key mode — show key preview or edit */}
          {mode === "own_key" && (
            <div className="mt-2">
              {editing ? (
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={ownKey}
                    onChange={(e) => setOwnKey(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleSaveOwnKey(); if (e.key === "Escape") { setEditing(false); setOwnKey(""); } }}
                    placeholder="Entrez votre cle API..."
                    className="flex-1 bg-slate-900/80 border border-blue-500/30 rounded-md px-2.5 py-1.5 text-[11px] text-white font-mono outline-none focus:border-blue-500/60"
                    data-testid={`own-key-input-${item.id}`}
                    autoFocus
                  />
                  <button
                    onClick={handleSaveOwnKey}
                    disabled={saving || !ownKey.trim()}
                    className="px-2.5 py-1.5 bg-blue-600/30 border border-blue-500/30 rounded-md text-[10px] text-blue-300 hover:bg-blue-600/50 transition-colors disabled:opacity-50"
                    data-testid={`save-key-${item.id}`}
                  >
                    {saving ? "..." : "Sauvegarder"}
                  </button>
                  <button
                    onClick={() => { setEditing(false); setOwnKey(""); if (!item.has_own_key) setMode("platform"); }}
                    className="px-1.5 py-1.5 text-slate-500 hover:text-slate-300 text-[10px]"
                  >
                    Annuler
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setShowKey(!showKey)}
                    className="text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <span className="font-mono text-[11px] text-blue-400/80">
                    {showKey ? (item.own_key_preview || "***") : "**********"}
                  </span>
                  <button
                    onClick={() => setEditing(true)}
                    className="ml-1 text-[10px] text-slate-500 hover:text-blue-400 transition-colors"
                    data-testid={`edit-key-${item.id}`}
                  >
                    Modifier
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Disabled mode */}
          {mode === "disabled" && (
            <div className="mt-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[11px] text-slate-500">Non disponible pour ce compte</span>
            </div>
          )}

          {/* Webhook tokens management button */}
          {hasManageWebhooks && (
            <button
              onClick={() => { setShowWebhooks(!showWebhooks); if (!showWebhooks) loadWebhookTokens(); }}
              className="mt-2 flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 transition-colors"
              data-testid="manage-webhooks-btn"
            >
              <LinkIcon className="w-3.5 h-3.5" />
              <span className="text-[11px] font-medium">{showWebhooks ? "Fermer" : "Gerer mes tokens webhook"}</span>
            </button>
          )}
        </div>
      </div>

      {/* Webhook tokens panel */}
      {hasManageWebhooks && showWebhooks && (
        <div className="mt-3 pt-3 border-t border-white/5 space-y-3" data-testid="webhook-tokens-panel">
          {webhooksLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-xs"><RefreshCw className="w-3 h-3 animate-spin" /> Chargement...</div>
          ) : (
            <>
              {/* Existing tokens */}
              {webhookTokens.length > 0 && (
                <div className="space-y-2">
                  {webhookTokens.map((t, i) => (
                    <div key={i} className="bg-slate-900/60 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-white">{t.name}</span>
                            <span className={`w-1.5 h-1.5 rounded-full ${t.active ? "bg-emerald-400" : "bg-red-400"}`} />
                          </div>
                          <div className="text-[10px] text-slate-500 font-mono mt-0.5">{t.token_preview}</div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => { navigator.clipboard.writeText(t.webhook_url); }} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" title="Copier l'URL webhook">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => toggleWebhookToken(t.name)} className={`p-1.5 rounded-lg transition-colors ${t.active ? "hover:bg-amber-500/20 text-amber-400" : "hover:bg-emerald-500/20 text-emerald-400"}`} title={t.active ? "Desactiver" : "Activer"}>
                            {t.active ? <WifiOff className="w-3.5 h-3.5" /> : <Wifi className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => deleteWebhookToken(t.name)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors" title="Supprimer">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-1.5 bg-slate-800/60 rounded px-2 py-1">
                        <code className="text-[10px] text-cyan-300/80 font-mono break-all">{t.webhook_url}</code>
                      </div>
                      {t.usage_count > 0 && <div className="text-[10px] text-slate-600 mt-1">{t.usage_count} appels{t.last_used ? ` — dernier: ${new Date(t.last_used).toLocaleString("fr-FR")}` : ""}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Created token alert */}
              {createdToken && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3" data-testid="new-webhook-token-alert">
                  <p className="text-xs text-cyan-300 font-medium mb-1">Token cree — copiez l'URL webhook :</p>
                  <div className="bg-slate-900/80 rounded px-2 py-1.5 mb-1">
                    <code className="text-[11px] text-cyan-200 font-mono break-all">{createdToken.webhook_url}</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigator.clipboard.writeText(createdToken.webhook_url)} className="px-3 py-1 bg-cyan-600/30 border border-cyan-500/30 rounded text-[10px] text-cyan-300 hover:bg-cyan-600/50 transition-colors">
                      Copier l'URL
                    </button>
                    <button onClick={() => setCreatedToken(null)} className="text-[10px] text-slate-500 hover:text-slate-300">Fermer</button>
                  </div>
                </div>
              )}

              {/* Create new token */}
              <div className="bg-slate-900/40 rounded-lg p-3 space-y-2">
                <p className="text-xs text-slate-400 font-medium">Nouveau token</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Nom (ex: Zapier ClubTravaux, Ma landing page...)"
                    value={newTokenName}
                    onChange={(e) => setNewTokenName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") createWebhookToken(); }}
                    className="flex-1 px-3 py-1.5 bg-slate-800/60 border border-white/5 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/30"
                    data-testid="new-webhook-name"
                  />
                  <button
                    onClick={createWebhookToken}
                    disabled={!newTokenName.trim() || saving}
                    className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 rounded-lg text-xs font-medium transition-colors shrink-0"
                    data-testid="create-webhook-btn"
                  >
                    {saving ? "..." : "Creer"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function TenantIntegrationsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/integrations/tenant-config`);
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error("Erreur chargement config tenant:", e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 text-slate-500 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-slate-500">
        <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-amber-400" />
        <p>Erreur de chargement</p>
        <button onClick={load} className="mt-3 px-4 py-2 bg-slate-700 rounded-lg text-sm hover:bg-slate-600 transition-colors text-white">
          Reessayer
        </button>
      </div>
    );
  }

  const integrations = data.integrations || [];
  const filtered = search.trim()
    ? integrations.filter(i => i.name.toLowerCase().includes(search.toLowerCase()) || i.description.toLowerCase().includes(search.toLowerCase()))
    : integrations;

  const platformCount = filtered.filter(i => i.mode === "platform").length;
  const ownCount = filtered.filter(i => i.mode === "own_key").length;
  const disabledCount = filtered.filter(i => i.mode === "disabled").length;

  return (
    <div className="space-y-6" data-testid="tenant-integrations-view">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">Integrations & Cles</h2>
          <p className="text-sm text-slate-400">
            Configurez vos integrations — utilisez les cles partagees ou fournissez les votres
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 bg-slate-800/60 border border-white/5 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/20 w-56"
              data-testid="tenant-integrations-search"
            />
          </div>
          <button onClick={load} className="p-2 rounded-lg bg-slate-800/60 border border-white/5 hover:border-white/20 transition-colors text-slate-400 hover:text-white" data-testid="tenant-integrations-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-emerald-400">{platformCount}</p>
          <p className="text-xs text-emerald-400/70 font-medium uppercase tracking-wider mt-1">Cle plateforme</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-400">{ownCount}</p>
          <p className="text-xs text-blue-400/70 font-medium uppercase tracking-wider mt-1">Propre cle</p>
        </div>
        <div className="bg-slate-500/10 border border-slate-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-slate-400">{disabledCount}</p>
          <p className="text-xs text-slate-400/70 font-medium uppercase tracking-wider mt-1">Desactivees</p>
        </div>
      </div>

      {/* Integration cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((item) => (
          <TenantIntegrationCard key={item.id} item={item} onUpdate={load} />
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// VUE ADMIN PLATEFORME — Vue existante complete
// ═══════════════════════════════════════════════════

function PlatformIntegrationsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/integrations/all-status`);
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error("Erreur chargement integrations:", e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filter = (items) => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(i => i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 text-slate-500 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-slate-500">
        <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-amber-400" />
        <p>Erreur de chargement des integrations</p>
        <button onClick={load} className="mt-3 px-4 py-2 bg-slate-700 rounded-lg text-sm hover:bg-slate-600 transition-colors text-white">
          Reessayer
        </button>
      </div>
    );
  }

  const connected = filter(data.connected || []);
  const enTest = filter(data.en_test || []);
  const aConnecter = filter(data.a_connecter || []);

  return (
    <div className="space-y-6" data-testid="integrations-cles-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">Integrations & Cles</h2>
          <p className="text-sm text-slate-400">
            {data.counts?.total || 0} integrations &middot; {data.counts?.connected || 0} connectees &middot; Statuts verifies dynamiquement
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 bg-slate-800/60 border border-white/5 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/20 w-56"
              data-testid="integrations-search"
            />
          </div>
          <button
            onClick={load}
            className="p-2 rounded-lg bg-slate-800/60 border border-white/5 hover:border-white/20 transition-colors text-slate-400 hover:text-white"
            title="Rafraichir"
            data-testid="integrations-refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-emerald-400" data-testid="count-connected">{data.counts?.connected || 0}</p>
          <p className="text-xs text-emerald-400/70 font-medium uppercase tracking-wider mt-1">Connectees</p>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-amber-400" data-testid="count-en-test">{data.counts?.en_test || 0}</p>
          <p className="text-xs text-amber-400/70 font-medium uppercase tracking-wider mt-1">En test</p>
        </div>
        <div className="bg-slate-500/10 border border-slate-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-slate-400" data-testid="count-a-connecter">{data.counts?.a_connecter || 0}</p>
          <p className="text-xs text-slate-400/70 font-medium uppercase tracking-wider mt-1">A connecter</p>
        </div>
      </div>

      {/* Sections */}
      <SectionBlock
        title="Connectees"
        subtitle="Services actifs et operationnels"
        count={connected.length}
        badge="Connectees"
        badgeColor="bg-emerald-600"
        icon={CheckCircle}
        items={connected}
        defaultOpen={true}
        onRefresh={load}
      />
      <SectionBlock
        title="En Test"
        subtitle="Configuration en cours ou validation necessaire"
        count={enTest.length}
        badge="En test"
        badgeColor="bg-amber-600"
        icon={AlertTriangle}
        items={enTest}
        defaultOpen={true}
        onRefresh={load}
      />
      <SectionBlock
        title="A Connecter"
        subtitle="Services disponibles pour extension"
        count={aConnecter.length}
        badge="A connecter"
        badgeColor="bg-slate-600"
        icon={Plus}
        items={aConnecter}
        defaultOpen={false}
        onRefresh={load}
      />
    </div>
  );
}
